import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { ThemeToggle } from '../../contexts/ThemeContext';
import { useTimezone } from '../../contexts/TimezoneContext';
import { useRole, ROLE_LABELS } from '../../contexts/RoleContext';
import { getEnvLabel } from '../../config';

export interface LayoutProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  navigation?: React.ReactNode;
  sidebar?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  fluid?: boolean;
}

export interface HeaderProps {
  title?: string;
  user?: {
    name?: string;
    email?: string;
  };
  onSignOut?: () => void;
  actions?: React.ReactNode;
  className?: string;
  /** Deployment environment (dev/tst/np/prd/sbx) — drives the env badge. */
  env?: string;
}

// Per-env badge styling. Keys are normalized (uppercase) env labels.
// dev -> gray, tst -> blue, np -> amber, prd -> red (red = production, be careful).
const ENV_BADGE_STYLES: Record<string, string> = {
  DEV: 'bg-neutral-100 text-neutral-600 ring-neutral-300 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-600',
  TST: 'bg-primary-50 text-primary-700 ring-primary-300 dark:bg-primary-900/30 dark:text-primary-300 dark:ring-primary-700',
  NP: 'bg-warning-100 text-warning-800 ring-warning-300 dark:bg-warning-900/30 dark:text-warning-300 dark:ring-warning-700',
  PRD: 'bg-danger-100 text-danger-700 ring-danger-300 dark:bg-danger-900/30 dark:text-danger-300 dark:ring-danger-700',
};
const ENV_BADGE_DEFAULT =
  'bg-neutral-100 text-neutral-600 ring-neutral-300 dark:bg-neutral-800 dark:text-neutral-300 dark:ring-neutral-600';

// ============================================================================
// Header Component
// ============================================================================

export const Header: React.FC<HeaderProps> = ({
  user,
  onSignOut,
  actions,
  className = '',
  env
}) => {
  const { connectionStatus, disconnect } = useWebSocket();
  const { role, email: roleEmail, displayName: roleDisplayName, hasMinimumRole } = useRole();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // THUB-186: display-timezone preference (per-user, localStorage-backed).
  const { timezone, setTimezone, abbreviation: tzAbbreviation, available: tzOptions } = useTimezone();
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [dropdownOpen]);

  // Close on Escape
  useEffect(() => {
    if (!dropdownOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [dropdownOpen]);

  // Use display name from Cognito (given_name + family_name) if available,
  // fall back to deriving from email
  const displayEmail = user?.email || user?.name || roleEmail || '';
  const displayName = roleDisplayName || (() => {
    if (!displayEmail) return '';
    const local = displayEmail.includes('@') ? displayEmail.split('@')[0] : displayEmail;
    const parts = local.split(/[._\-+]/);
    if (parts.length >= 2) {
      return parts.map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ');
    }
    return local.charAt(0).toUpperCase() + local.slice(1);
  })();
  const initials = (() => {
    // Use display name for initials if available (handles McConnell correctly)
    const src = displayName || displayEmail;
    if (!src) return '?';
    const parts = src.split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    const local = src.includes('@') ? src.split('@')[0] : src;
    const emailParts = local.split(/[._\-+]/);
    if (emailParts.length >= 2) {
      return (emailParts[0][0] + emailParts[1][0]).toUpperCase();
    }
    return local.substring(0, 2).toUpperCase();
  })();

  // WebSocket status dot color
  const statusDot = (() => {
    switch (connectionStatus) {
      case 'connected':
        return 'bg-success-500';
      case 'connecting':
      case 'reconnecting':
        return 'bg-warning-500 animate-pulse';
      default:
        return 'bg-neutral-400 dark:bg-neutral-600';
    }
  })();

  const statusLabel = (() => {
    switch (connectionStatus) {
      case 'connected': return 'Live';
      case 'connecting': return 'Connecting';
      case 'reconnecting': return 'Reconnecting';
      default: return 'Offline';
    }
  })();

  // Environment badge (DEV/TST/NP/PRD/SBX). Hidden when env is absent (local dev).
  const envLabel = getEnvLabel(env);
  const envBadgeStyle = envLabel
    ? (ENV_BADGE_STYLES[envLabel] || ENV_BADGE_DEFAULT)
    : '';

  return (
    <header className={`
      sticky top-0 z-30
      bg-white dark:bg-neutral-900
      border-b border-neutral-200 dark:border-neutral-800
      ${className}
    `}>
      <div className="
        flex items-center justify-between
        h-14 px-4 lg:px-6
        max-w-[1920px] mx-auto
      ">
        {/* Left: Brand */}
        <div className="flex items-center gap-2.5">
          <div className="
            flex items-center justify-center
            w-8 h-8 rounded-lg overflow-hidden
            bg-white
            ring-1 ring-neutral-200 dark:ring-neutral-700
          ">
            <img
              src="/favicon-180.png"
              alt=""
              className="w-full h-full"
              style={{ objectFit: 'cover' }}
            />
          </div>
          <div className="hidden sm:flex flex-col leading-none">
            <span className="text-[10px] font-semibold tracking-wider uppercase text-cgic-primary dark:text-primary-300">
              CGIC
            </span>
            <span className="text-[15px] font-bold text-neutral-900 dark:text-white -mt-0.5">
              TransferHub
            </span>
          </div>

          {/* Environment badge */}
          {envLabel && (
            <span
              className={`
                ml-0.5 px-1.5 py-0.5
                rounded
                text-[10px] font-bold tracking-wider uppercase
                ring-1 ring-inset
                select-none
                ${envBadgeStyle}
              `}
              title={`Environment: ${envLabel}`}
              aria-label={`Environment: ${envLabel}`}
            >
              {envLabel}
            </span>
          )}
        </div>

        {/* Right: Controls */}
        <div className="flex items-center gap-1.5">
          {actions}

          {/* WebSocket status dot + label */}
          <div
            className="
              flex items-center gap-1.5
              px-2.5 py-1.5
              rounded-md
              text-xs font-medium
              text-neutral-500 dark:text-neutral-400
              select-none
            "
            role="status"
            aria-label={`Connection: ${statusLabel}`}
            title={`WebSocket: ${statusLabel}`}
          >
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusDot}`} aria-hidden="true" />
            <span className="hidden sm:inline">{statusLabel}</span>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-neutral-200 dark:bg-neutral-700 mx-1" />

          {/* Theme toggle */}
          <ThemeToggle className="
            h-9 w-9
            flex items-center justify-center
            rounded-lg
            text-neutral-500 dark:text-neutral-400
            hover:bg-neutral-100 dark:hover:bg-neutral-800
            hover:text-neutral-900 dark:hover:text-white
            transition-colors duration-150
          " />

          {/* User dropdown */}
          {user && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(prev => !prev)}
                className={`
                  flex items-center gap-2
                  h-9 pl-1 pr-2.5
                  rounded-lg
                  transition-colors duration-150
                  focus:outline-none focus-visible:ring-2 focus-visible:ring-cgic-primary focus-visible:ring-offset-2
                  dark:focus-visible:ring-offset-neutral-900
                  ${dropdownOpen
                    ? 'bg-neutral-100 dark:bg-neutral-800'
                    : 'hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  }
                `}
                aria-expanded={dropdownOpen}
                aria-haspopup="true"
              >
                {/* Avatar circle */}
                <span className="
                  flex items-center justify-center
                  w-7 h-7 rounded-md
                  bg-gradient-to-br from-cgic-primary to-primary-700
                  text-white text-[11px] font-bold
                  select-none
                ">
                  {initials}
                </span>

                {/* User name - hidden on small screens */}
                <span className="hidden md:block text-[13px] font-medium text-neutral-700 dark:text-neutral-300 max-w-[180px] truncate">
                  {displayName || displayEmail}
                </span>

                {/* Chevron */}
                <svg
                  className={`
                    w-3.5 h-3.5 text-neutral-400 dark:text-neutral-500
                    transition-transform duration-200
                    ${dropdownOpen ? 'rotate-180' : ''}
                  `}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </button>

              {/* Dropdown menu */}
              {dropdownOpen && (
                <div className="
                  absolute right-0 top-full mt-1.5
                  w-56
                  bg-white dark:bg-neutral-800
                  border border-neutral-200 dark:border-neutral-700
                  rounded-lg
                  shadow-lg shadow-neutral-900/10 dark:shadow-neutral-950/40
                  py-1.5
                  z-50
                  animate-in fade-in slide-in-from-top-1
                ">
                  {/* User info section */}
                  <div className="px-3.5 py-2.5 border-b border-neutral-100 dark:border-neutral-700/50">
                    <p className="text-[13px] font-medium text-neutral-900 dark:text-white truncate">
                      {user.name || displayEmail}
                    </p>
                    {user.name && user.email && (
                      <p className="text-[12px] text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                        {user.email}
                      </p>
                    )}
                    {role && (
                      <p className="text-[11px] text-primary-600 dark:text-primary-400 font-medium mt-1">
                        {ROLE_LABELS[role]}
                      </p>
                    )}
                  </div>

                  {/* Display timezone (THUB-186).
                      Lives in the user menu because it is a per-user display preference,
                      not a system setting. Instants are UTC end to end (THUB-272); this
                      only changes how they are rendered. */}
                  <div className="px-3.5 pt-2.5 pb-1">
                    <label
                      htmlFor="tz-select"
                      className="block text-[11px] font-medium uppercase tracking-wide text-neutral-400 dark:text-neutral-500 mb-1"
                    >
                      Display timezone
                    </label>
                    <select
                      id="tz-select"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                      className="
                        w-full px-2 py-1.5
                        text-[12px]
                        bg-white dark:bg-neutral-900
                        border border-neutral-200 dark:border-neutral-700
                        text-neutral-700 dark:text-neutral-300
                        rounded-md
                        focus:outline-none focus:ring-2 focus:ring-primary-500
                      "
                    >
                      {tzOptions.map((tz) => (
                        <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                    <p className="text-[11px] text-neutral-400 dark:text-neutral-500 mt-1">
                      All times shown in <span className="font-medium">{tzAbbreviation}</span>
                    </p>
                  </div>

                  {/* Admin link - only for SystemAdmin */}
                  {hasMinimumRole('system_admin') && (
                    <div className="px-1.5 pt-1.5">
                      <button
                        onClick={() => {
                          setDropdownOpen(false);
                          navigate('/admin');
                        }}
                        className="
                          w-full flex items-center gap-2.5
                          px-2.5 py-2
                          text-[13px] font-medium text-left
                          text-neutral-600 dark:text-neutral-300
                          hover:text-primary-600 dark:hover:text-primary-400
                          hover:bg-primary-50 dark:hover:bg-primary-900/20
                          rounded-md
                          transition-colors duration-150
                        "
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="3" />
                          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                        </svg>
                        Admin
                      </button>
                    </div>
                  )}

                  {/* Sign out */}
                  {onSignOut && (
                    <div className="px-1.5 pt-1.5">
                      <button
                        onClick={() => {
                          setDropdownOpen(false);
                          disconnect();
                          onSignOut();
                        }}
                        className="
                          w-full flex items-center gap-2.5
                          px-2.5 py-2
                          text-[13px] font-medium text-left
                          text-neutral-600 dark:text-neutral-300
                          hover:text-danger-600 dark:hover:text-danger-400
                          hover:bg-danger-50 dark:hover:bg-danger-900/20
                          rounded-md
                          transition-colors duration-150
                        "
                      >
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                          <polyline points="16,17 21,12 16,7" />
                          <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// ============================================================================
// Layout Component
// ============================================================================

export const Layout: React.FC<LayoutProps> = ({
  children,
  header,
  navigation,
  sidebar,
  footer,
  className = '',
  fluid = false,
}) => {
  return (
    <div className={`
      flex flex-col min-h-screen
      bg-neutral-50 dark:bg-neutral-950
      ${className}
    `}>
      {/* Header */}
      {header}

      {/* Navigation bar (horizontal, under header) */}
      {navigation && (
        <div className="
          sticky top-14 z-20
          bg-white dark:bg-neutral-900
          border-b border-neutral-200 dark:border-neutral-800
        ">
          {navigation}
        </div>
      )}

      {/* Main content area with optional sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - responsive widths */}
        {sidebar && (
          <aside className="
            hidden lg:block
            flex-shrink-0
            w-64 xl:w-72
            h-[calc(100vh-3.5rem)]
            sticky top-14
            overflow-y-auto
            bg-white dark:bg-neutral-900
            border-r border-neutral-200 dark:border-neutral-800
          ">
            {sidebar}
          </aside>
        )}

        {/* Main content */}
        <main
          className="flex-1 min-w-0 overflow-y-auto"
          role="main"
        >
          <div className={`
            ${fluid
              ? 'px-4 sm:px-6 lg:px-8'
              : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'
            }
            py-6 lg:py-8
          `}>
            {children}
          </div>
        </main>
      </div>

      {/* Footer */}
      {footer && (
        <footer className="
          mt-auto
          bg-white dark:bg-neutral-900
          border-t border-neutral-200 dark:border-neutral-800
          py-4 px-4 sm:px-6 lg:px-8
        ">
          {footer}
        </footer>
      )}
    </div>
  );
};

export default Layout;
